import { normalizeReferrerUri } from './tfn';

/**
 * Sanitizers for the values written to `order.trackingAttrs` (ELNKWEB-3215).
 *
 * Kept byte-identical with the copies in the sibling checkouts -
 * app-checkout.earthlink.net and app-checkout.earthlink.com - so diff them before
 * and after editing this file. A change here that lands in only one site is how
 * the three drifted apart in the first place. Rationale and the full contract:
 * ELNKWEB-3215.
 *
 * One sanctioned difference between the copies: the `normalizeReferrerUri` import
 * above, which `.com` keeps in `../util/util`.
 */

/** Longest referrer we will persist. Bounds the JSONB column against a junk URL. */
const MAX_REFERRER_LENGTH = 256;

/**
 * The displayed toll-free number reduced to digits.
 *
 * Deliberately does no length normalization and does not strip a leading `1`:
 * this field records what was on screen, verbatim. `'n/a'` - the sentinel the
 * `.com` site used to write - contains no digits and therefore collapses to `''`,
 * which callers treat as "nothing to write".
 */
export const tfnDigits = (value) => String(value ?? '').replace(/\D/g, '');

/**
 * The referrer as recorded on the order.
 *
 * Raw, not map-keyed: an unmapped referrer is still worth recording, so this
 * never consults `referrerUris` or `influencerPromoCodes`. `normalizeReferrerUri`
 * drops the scheme and everything from the first `/`, `?` or `#`, which leaves an
 * influencer token like `teamearthlink.com.jack` intact (the landing page sets
 * that dot form itself - ELNKWEB-3137) while reducing a URL-shaped referrer to
 * its bare host. A path is NOT converted into a dot suffix.
 *
 * The character allowlist is defensive rather than load-bearing: normalize
 * already removes the path, but it does not remove the characters an injected
 * referrer could otherwise carry into the column.
 *
 * @returns {string} sanitized referrer, or '' - callers omit the key on ''.
 */
export const sanitizeTrackingReferrer = (raw) =>
  normalizeReferrerUri(String(raw ?? ''))
    .replace(/[^a-z0-9.-]/g, '')
    .slice(0, MAX_REFERRER_LENGTH);
